import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, ArrowRight } from 'lucide-react';
import { events } from '../mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export const Events = () => {
  const [filter, setFilter] = useState('all');

  const eventTypes = [
    { id: 'all', label: 'All Events' },
    { id: 'gathering', label: 'Gatherings' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'Langer', label: 'Langer' },
    { id: 'religious', label: 'Religious' }
  ];

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(event => event.type === filter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getEventColor = (type) => {
    const colors = {
      gathering: 'bg-blue-100 text-blue-700',
      healthcare: 'bg-red-100 text-red-700',
      education: 'bg-purple-100 text-purple-700',
      religious: 'bg-emerald-100 text-emerald-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <section id="events" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-2 block">
            Upcoming Events
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Join Our Community Events
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-emerald-600 to-teal-600 mx-auto rounded-full mb-6"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-600">
            Participate in our diverse range of community events, from educational workshops 
            to health camps and cultural gatherings.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {eventTypes.map((type) => (
            <Button
              key={type.id}
              variant={filter === type.id ? 'default' : 'outline'}
              onClick={() => setFilter(type.id)}
              className={`rounded-full px-6 transition-all duration-300 ${
                filter === type.id
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
                  : 'border-gray-300 text-gray-700 hover:border-emerald-600 hover:text-emerald-600'
              }`}
            >
              {type.label}
            </Button>
          ))}
        </div>

        {/* Events Grid */}
        {filteredEvents.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-8">
            {filteredEvents.map((event) => (
              <Card 
                key={event.id}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Event Header */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-200/30 rounded-full -mr-20 -mt-20"></div>
                    <div className="relative">
                      <Badge className={`${getEventColor(event.type)} mb-4`}>
                        {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                      </Badge>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-emerald-700 transition-colors duration-300">
                        {event.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-start space-x-3">
                      <Calendar className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-gray-500">Date</div>
                        <div className="font-semibold text-gray-900">{formatDate(event.date)}</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-gray-500">Time</div>
                        <div className="font-semibold text-gray-900">{event.time}</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-gray-500">Location</div>
                        <div className="font-semibold text-gray-900">{event.location}</div>
                      </div>
                    </div>

                    <Button 
                      className="w-full mt-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white group-hover:shadow-lg transition-all duration-300"
                    >
                      Register for Event
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No events found in this category.</p>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-slate-900 to-emerald-900 rounded-2xl p-10 text-center text-white shadow-xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Stay Updated with Our Events
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Subscribe to our newsletter to receive updates about upcoming events, programs, 
            and community initiatives.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <Button 
              size="lg"
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-8 whitespace-nowrap"
            >
              Subscribe
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
